import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';
import { blogApi, uploadApi } from '@/services/api';
import { toast } from 'sonner';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    excerpt_en: '',
    excerpt_ar: '',
    content_en: '',
    content_ar: '',
    cover_image: '',
    author: 'محمد سمير',
    published: false,
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await blogApi.getAllAdmin();
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPost) {
        await blogApi.update(editingPost.id, formData);
        toast.success('Post updated successfully');
      } else {
        await blogApi.create(formData);
        toast.success('Post created successfully');
      }
      await fetchPosts();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await blogApi.remove(id);
      toast.success('Post deleted');
      await fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.image(file);
      setFormData((prev) => ({ ...prev, cover_image: res.data.url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setFormData({
      title_en: post.title_en,
      title_ar: post.title_ar,
      excerpt_en: post.excerpt_en,
      excerpt_ar: post.excerpt_ar,
      content_en: post.content_en,
      content_ar: post.content_ar,
      cover_image: post.cover_image || '',
      author: post.author,
      published: post.published,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setFormData({
      title_en: '',
      title_ar: '',
      excerpt_en: '',
      excerpt_ar: '',
      content_en: '',
      content_ar: '',
      cover_image: '',
      author: 'محمد سمير',
      published: false,
    });
  };

  const field = (key, label, type = 'input', extra = {}) => (
    <div>
      <label className="block text-white/60 text-sm mb-2">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={formData[key]}
          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
          rows={extra.rows || 4}
          dir={extra.dir}
          required={extra.required}
          className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-none"
        />
      ) : (
        <input
          type={extra.inputType || 'text'}
          value={formData[key]}
          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
          dir={extra.dir}
          required={extra.required}
          className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
        />
      )}
    </div>
  );

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog Posts</h1>
          <p className="text-white/60">Manage your blog content</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-gold text-charcoal px-4 py-2 font-medium hover:bg-gold/90 transition-colors"
        >
          <Plus size={20} />
          New Post
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          <p className="text-lg">No blog posts yet</p>
          <p className="text-sm mt-2">Click "New Post" to create your first article</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/5 border border-white/10 p-4 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4 min-w-0">
                {post.cover_image && (
                  <img
                    src={post.cover_image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-16 h-16 object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium truncate">{post.title_en}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 flex-shrink-0 ${
                        post.published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-white/40 text-sm truncate">{post.title_ar}</p>
                  {post.slug && (
                    <p className="text-white/30 text-xs mt-0.5">/{post.slug}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => openEditModal(post)}
                  className="w-9 h-9 bg-white/10 flex items-center justify-center hover:bg-gold hover:text-charcoal transition-colors text-white"
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="w-9 h-9 bg-white/10 flex items-center justify-center hover:bg-red-500 transition-colors text-white"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-panel w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">
                {editingPost ? 'Edit Post' : 'New Blog Post'}
              </h2>
              <button onClick={closeModal} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {field('title_en', 'Title (English)', 'input', { required: true })}
                {field('title_ar', 'Title (Arabic)', 'input', { required: true, dir: 'rtl' })}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {field('excerpt_en', 'Excerpt (English)', 'textarea', { rows: 2, required: true })}
                {field('excerpt_ar', 'Excerpt (Arabic)', 'textarea', { rows: 2, required: true, dir: 'rtl' })}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {field('content_en', 'Content (English)', 'textarea', { rows: 6, required: true })}
                {field('content_ar', 'Content (Arabic)', 'textarea', { rows: 6, required: true, dir: 'rtl' })}
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-white/60 text-sm mb-2">Cover Image</label>
                <div className="flex gap-3 items-start">
                  <input
                    type="text"
                    value={formData.cover_image}
                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                    placeholder="Paste URL or upload below"
                    className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  />
                  <label className="flex-shrink-0 cursor-pointer bg-white/10 border border-white/10 text-white px-4 py-3 hover:bg-white/20 transition-colors text-sm">
                    {uploading ? 'Uploading…' : 'Upload File'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
                  </label>
                </div>
                {formData.cover_image && (
                  <img
                    src={formData.cover_image}
                    alt="Cover preview"
                    loading="lazy"
                    className="mt-3 h-32 w-full object-cover"
                  />
                )}
              </div>

              {field('author', 'Author')}

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="published" className="text-white flex items-center gap-2">
                  {formData.published ? <Eye size={16} className="text-green-400" /> : <EyeOff size={16} className="text-white/40" />}
                  {formData.published ? 'Published (visible on site)' : 'Draft (hidden from public)'}
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-white/20 text-white py-3 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gold text-charcoal py-3 font-medium hover:bg-gold/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving…' : 'Save Post'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
